import { Link } from 'react-router-dom'
import Icon, { type IconName } from '../docs/Icon'
import { missions } from '@/content/missions'
import { contrastPairs } from '@/content/theme'
import { siteRoutes } from '@/content/system'
import Cased from '../ui/Cased'

const practices: Array<{ icon: IconName; title: string; text: string }> = [
  {
    icon: 'check',
    title: 'Contrast, checked on every build',
    text: `${(missions.length * 2 * contrastPairs.length).toLocaleString('en-US')} tests pass on every build.`,
  },
  {
    icon: 'menu',
    title: 'Keyboard first',
    text: 'Every control works from the keyboard, with a visible focus ring, a skip link and dialogs that hold and return focus.',
  },
  {
    icon: 'file',
    title: 'Screen reader ready',
    text: 'Semantic landmarks, one clear heading per page, labeled fields, errors tied to their inputs and status announced as it changes.',
  },
  {
    icon: 'layers',
    title: 'Reflows to any screen',
    text: 'Pages work at 320 pixels wide and at 400 percent zoom without scrolling sideways.',
  },
]

/** Section 508 and WCAG, stated as the practices the system builds in. */
export default function AccessibilityBand() {
  return (
    <section className="mds-section home-access" aria-labelledby="access-title">
      <div className="mds-container">
        <div className="home-access__head">
          <div className="mds-stack">
            <span className="mds-eyebrow">Accessibility</span>
            <h2 id="access-title" className="mds-display">
              <Cased>Made for every American.</Cased>
            </h2>
          </div>
          <div className="mds-stack">
            <p className="mds-lead">
              Government websites serve everyone. MDS is built to the{' '}
              <a className="mds-link" href="https://www.access-board.gov/ict/">
                Revised Section 508 Standards
              </a>{' '}
              and{' '}
              <a className="mds-link" href="https://www.w3.org/TR/WCAG21/">
                WCAG 2.1 Level AA
              </a>
              , and every page of this site is scanned with axe on each change, at desktop and phone sizes.
            </p>
            <p className="home-access__try">
              <span className="mds-kbd">Tab</span> Try it: this whole page works from the keyboard.
            </p>
          </div>
        </div>
        <ul className="home-access__grid">
          {practices.map((practice) => (
            <li key={practice.title} className="home-access__item">
              <span className="home-access__icon" aria-hidden="true">
                <Icon name={practice.icon} />
              </span>
              <h3 className="mds-h3"><Cased>{practice.title}</Cased></h3>
              <p className="mds-muted">{practice.text}</p>
            </li>
          ))}
        </ul>
        <p className="home-access__foot">
          <span className="mds-muted">
            Automated checks cover {siteRoutes.length} routes; each adopting site completes its own Section 508 review.
          </span>{' '}
          <Link to="/accessibility" className="mds-link">
            Read the accessibility statement
          </Link>
        </p>
      </div>
    </section>
  )
}
